import React, { useState } from 'react';
import { LifeBuoy, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { apiClient, formatApiError } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';

export default function Help() {
  const { t } = useLanguage();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = title.trim().length >= 3 && body.trim().length >= 10;

  const submit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await apiClient.post('/help', { title: title.trim(), body: body.trim() });
      setTitle('');
      setBody('');
      toast.success(t('helpSubmitted'));
    } catch (err) {
      toast.error(formatApiError(err) || t('helpSubmitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8" data-testid="help-page">
      <div className="mb-8">
        <div className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.18em] text-orange-600">
          <LifeBuoy className="h-4 w-4" /> {t('help')}
        </div>
        <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">{t('helpPageTitle')}</h1>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-500">{t('helpPageIntro')}</p>
      </div>

      <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="space-y-5">
          <label className="block">
            <span className="mb-2 block text-xs font-black uppercase tracking-wider text-slate-500">{t('queryTitle')}</span>
            <Input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              maxLength={160}
              placeholder={t('queryTitlePlaceholder')}
              className="h-11"
              data-testid="help-title"
              required
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-xs font-black uppercase tracking-wider text-slate-500">{t('queryBody')}</span>
            <Textarea
              value={body}
              onChange={(event) => setBody(event.target.value)}
              maxLength={5000}
              placeholder={t('queryBodyPlaceholder')}
              className="min-h-44 resize-y"
              data-testid="help-body"
              required
            />
          </label>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs leading-relaxed text-slate-400">{t('helpPrivateCopy')}</p>
            <Button type="submit" disabled={!canSubmit || submitting} className="h-11 bg-slate-900 px-5 text-white hover:bg-slate-800">
              <Send className="mr-2 h-4 w-4" />
              {submitting ? t('sending') : t('submitQuery')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
